import React, { useState, useEffect } from 'react';
import { X, Volume2, Mic, FileText, Play, Link as LinkIcon, ChevronDown, Sparkles, AlertCircle, Bot, Clock } from 'lucide-react';

/**
 * VoiceBotModal - Modal for configuring and launching voice bot into a meeting
 * 
 * This modal is shown when users with access click the Voice Bot button.
 * It allows them to:
 * - Enter a meeting link
 * - Select a bot
 * - Configure voice settings (enable_speak is always true)
 * - Set recording duration
 * - Launch the voice bot
 */
const VoiceBotModal = ({
    isOpen,
    onClose,
    darkMode,
    bots = [],
    onLaunch,
    loading = false
}) => {
    const [meetLink, setMeetLink] = useState('');
    const [selectedBotId, setSelectedBotId] = useState('');
    const [enableRecording, setEnableRecording] = useState(true);
    const [enableTranscript, setEnableTranscript] = useState(true);
    const [duration, setDuration] = useState(15);
    const [error, setError] = useState('');

    // Auto-select first bot when available
    useEffect(() => {
        if (bots.length > 0 && !selectedBotId) {
            setSelectedBotId(bots[0]._id || bots[0].id);
        }
    }, [bots, selectedBotId]);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            setMeetLink('');
            setError('');
        }
    }, [isOpen]);

    const handleLaunch = () => {
        if (!meetLink.trim()) {
            setError('Please enter a meeting link');
            return;
        }
        if (!selectedBotId) {
            setError('Please select a bot');
            return;
        }

        setError('');
        onLaunch({
            meetlink: meetLink,
            bot_id: selectedBotId,
            meeting_title: 'Voice Bot Meeting',
            min_record_time_seconds: duration * 60,
            bot_config: {
                enable_recording: enableRecording,
                enable_transcript: true, // Backend auto-enables when enable_speak is true
                enable_speak: true // Always true for voice bot
            }
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col ${darkMode ? 'bg-slate-900' : 'bg-white'
                    }`}
            >
                {/* Decorative gradient */}
                <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-br from-violet-600/20 via-purple-500/20 to-indigo-600/20 pointer-events-none" />

                {/* Close button */}
                <button
                    onClick={onClose}
                    className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-all hover:rotate-90 ${darkMode
                        ? 'hover:bg-white/10 text-white/70 hover:text-white'
                        : 'hover:bg-black/5 text-black/50 hover:text-black'
                        }`}
                >
                    <X size={18} />
                </button>

                {/* Scrollable content area */}
                <div className="relative p-4 overflow-y-auto flex-1 custom-scrollbar">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25">
                            <Volume2 size={20} />
                        </div>
                        <div>
                            <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                Send Voice Bot
                            </h2>
                            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                AI will join and speak in your meeting
                            </p>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="space-y-3">
                        {/* Meeting Link Input */}
                        <div>
                            <label className={`block text-[10px] font-medium uppercase tracking-wider mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Meeting Link
                            </label>
                            <div className="relative">
                                <LinkIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                                <input
                                    type="url"
                                    value={meetLink}
                                    onChange={(e) => setMeetLink(e.target.value)}
                                    placeholder="https://meet.google.com/..."
                                    className={`w-full pl-9 pr-3 py-2.5 rounded-lg border text-sm transition-all ${darkMode
                                        ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500 focus:border-purple-500'
                                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-purple-500'
                                        } focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
                                />
                            </div>
                        </div>

                        {/* Bot Selection */}
                        <div>
                            <label className={`block text-[10px] font-medium uppercase tracking-wider mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Select Bot
                            </label>
                            <div className={`relative flex items-center rounded-lg border transition-all ${darkMode
                                ? 'bg-slate-800 border-slate-700 focus-within:border-purple-500'
                                : 'bg-slate-50 border-slate-200 focus-within:border-purple-500'
                                }`}>
                                <Bot className={`ml-3 w-4 h-4 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                                <select
                                    value={selectedBotId}
                                    onChange={(e) => setSelectedBotId(e.target.value)}
                                    className={`w-full appearance-none pl-2 pr-8 py-2.5 rounded-lg text-sm bg-transparent border-none focus:ring-0 ${darkMode ? 'text-white' : 'text-slate-900'
                                        }`}
                                >
                                    {bots.length === 0 && <option value="">No bots available</option>}
                                    {bots.map(bot => (
                                        <option key={bot._id || bot.id} value={bot._id || bot.id} className="text-black">
                                            {bot.name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" />
                            </div>
                        </div>

                        {/* Voice Bot Features - More compact */}
                        <div className={`p-3 rounded-lg border ${darkMode ? 'bg-purple-500/10 border-purple-500/20' : 'bg-purple-50 border-purple-100'}`}>
                            <div className="flex items-center gap-1.5 mb-2">
                                <Sparkles size={12} className="text-purple-500" />
                                <span className={`text-[10px] font-medium uppercase tracking-wider ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                    Voice Bot Features
                                </span>
                            </div>
                            <div className="grid grid-cols-1 gap-1.5">
                                <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                    <div className="w-1 h-1 rounded-full bg-purple-500 flex-shrink-0" />
                                    <span>AI will <strong>speak</strong> during the meeting</span>
                                </div>
                                <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                    <div className="w-1 h-1 rounded-full bg-purple-500 flex-shrink-0" />
                                    <span>Real-time <strong>transcription</strong> enabled</span>
                                </div>
                                <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                    <div className="w-1 h-1 rounded-full bg-purple-500 flex-shrink-0" />
                                    <span>Listens and responds <strong>contextually</strong></span>
                                </div>
                            </div>
                        </div>

                        {/* Duration Slider - More compact */}
                        <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-1.5">
                                    <Clock size={12} className={darkMode ? 'text-slate-400' : 'text-slate-500'} />
                                    <label className={`text-[10px] font-medium uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        Auto-stop after
                                    </label>
                                </div>
                                <span className={`text-xs font-bold ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                    {duration} min
                                </span>
                            </div>
                            <input
                                type="range"
                                min="5"
                                max="60"
                                step="5"
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                                className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
                            />
                            <div className={`flex justify-between text-[9px] mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                <span>5 min</span>
                                <span>60 min</span>
                            </div>
                        </div>

                        {/* Recording Toggle - More compact */}
                        <div className={`flex items-center justify-between p-2.5 rounded-lg border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                            <div className="flex items-center gap-2">
                                <Mic size={14} className={enableRecording ? 'text-red-500' : (darkMode ? 'text-slate-500' : 'text-slate-400')} />
                                <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Record Audio</span>
                            </div>
                            <button
                                onClick={() => setEnableRecording(!enableRecording)}
                                className={`w-9 h-[18px] rounded-full relative transition-colors duration-200 ${enableRecording ? 'bg-red-500' : (darkMode ? 'bg-slate-700' : 'bg-slate-300')
                                    }`}
                            >
                                <div className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-white transition-transform duration-200 ${enableRecording ? 'translate-x-[18px]' : 'translate-x-0'
                                    }`} />
                            </button>
                        </div>

                        {/* Error message */}
                        {error && (
                            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
                                <AlertCircle size={14} className="text-red-500 flex-shrink-0" />
                                <span className="text-xs text-red-500">{error}</span>
                            </div>
                        )}

                        {/* Launch Button */}
                        <button
                            onClick={handleLaunch}
                            disabled={loading}
                            className={`w-full py-3 rounded-lg font-semibold text-sm text-white shadow-lg transition-all flex items-center justify-center gap-2 ${loading
                                ? 'bg-slate-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:from-violet-500 hover:via-purple-500 hover:to-indigo-500 shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-[1.02] active:scale-[0.98]'
                                }`}
                        >
                            {loading ? (
                                <span className="animate-pulse">Launching...</span>
                            ) : (
                                <>
                                    <Play size={16} fill="currentColor" />
                                    Launch Voice Bot
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom scrollbar styles */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: ${darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'};
                }
            `}</style>
        </div>
    );
};

export default VoiceBotModal;
